import MarkdownPreview from '@uiw/react-markdown-preview';
import rehypeSanitize from "rehype-sanitize";
import rehypeExternalLinks from 'rehype-external-links'
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversations, useTheme } from '../../store/hooks';
import './MarkdownPresenter.scss';
import { getCodeString } from "rehype-rewrite";
import katex from 'katex';
import 'katex/dist/katex.min.css' // `katex` does not import the CSS for you
import CodeSection from './CodeSection';
import TableSection from './TableSection';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { APPROVED_IMAGE_HOSTS } from './markdown/approvedImageHosts';
import { markdownSanitizeSchema } from './markdown/markdownSanitizeSchema';
import { rehypeBlockExternalImages } from './markdown/rehypeBlockExternalImages';

interface MarkdownPresenterProps {
  text: string;
}

// renders the markdown text
const MarkdownPresenter: FC<MarkdownPresenterProps> = ({ text }) => {
  // uses a fake URL prefix to identify the documents that should be downloaded
  // the fake URL is set from the back-end
  const documentUrlPrefix = "https://sygpt-sandbox.com/";
  const { theme } = useTheme();
  const { t } = useTranslation();
  const rehypePlugins = [
    [rehypeSanitize, markdownSanitizeSchema],
    () => rehypeBlockExternalImages({ approvedHosts: APPROVED_IMAGE_HOSTS, label: t('markdown.blockedImage') }),
    () => rehypeExternalLinks({ target: '_blank', rel: ['noopener', 'noreferrer', 'nofollow'] }),
  ];
  const { activeChat } = useConversations();

  const downloadFile = useCallback((event: React.MouseEvent, href: string) => {
    event.preventDefault();
    performEnterpriseGPTApiCall(async (client) => {
      const filename = href.replace(documentUrlPrefix, "") // get the filename, as the structure is https://sygpt-sandbox.com/{filename}
      if (activeChat && filename) {
          const attachment = await client.downloadAttachment(activeChat?.id, filename);
          const url = document.createElement('a'); 
          url.href = URL.createObjectURL(attachment.data); 
          url.download = filename; 
          document.body.appendChild(url); 
          url.click(); 
          document.body.removeChild(url);
      }
  });
  }, []);

  return (
    <MarkdownPreview
      source={text}
      rehypePlugins={rehypePlugins}
      style={{
        backgroundColor: 'transparent',
      }}
      wrapperElement={{
        'data-color-mode': theme,
      }}
      components={{
        // renders the table
        table: ({ children = [] }) => {
          return <TableSection>{children}</TableSection>; 
        },
        // renders the link
        a: ({ children = [], ...props }) => {
          if (props.href?.startsWith(documentUrlPrefix)){
            return <a {...props} target="_blank" onClick={(event) => downloadFile(event, props.href!)}>{children}</a>;
          }
          else {
            return <a {...props} target="_blank">{children}</a>;
          }          
        },
        // renders the code block. Code is returned from GPT as Latex
        // katex library is used to render the Latex code
        code: ({ children = [], className, ...props }: any) => {
          if (typeof children === 'string' && /^\$\$(.*)\$\$/.test(children)) {
            const html = katex.renderToString(children.replace(/^\$\$(.*)\$\$/, '$1'), {
              throwOnError: false,
            });
            return <code dangerouslySetInnerHTML={{ __html: html }} style={{ background: 'transparent' }} />;
          }
          const code = props.node && props.node.children ? getCodeString(props.node.children) : children;
          if (
            typeof code === 'string' &&
            typeof className === 'string' &&
            /^language-katex/.test(className.toLocaleLowerCase())
          ) {
            const html = katex.renderToString(code, {
              throwOnError: false,
            });
            return <code style={{ fontSize: '150%' }} dangerouslySetInnerHTML={{ __html: html }} />;
          }
          const match = /language-(\w+)/.exec(className || '')
          if (match && Array.isArray(children)) {
            return <CodeSection className={className}>{children}</CodeSection>;
          }
          return <code className={String(className)}>{children}</code>;
        },
      }}
    />
  );
};

export default MarkdownPresenter;